import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { 
  BASE_URL, 
  GET_PATIENT_TRACK, 
  GET_PATIENT_HISTORY,
  GET_PHARMACY_SALES_REPORT,
  GET_INVOICE_DETAILS,
  GET_CARDS
} from '../utlilities/config';
import { 
  FiUser, FiCalendar, FiArrowLeft, FiClock, FiDollarSign,
  FiMapPin, FiPhone, FiActivity, FiPackage, FiFileText,
  FiCheckCircle, FiXCircle, FiAlertCircle, FiDownload,
  FiChevronDown, FiChevronUp, FiCreditCard
} from "react-icons/fi";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Slide } from 'react-toastify';
import { TbCurrencyRupee } from "react-icons/tb";
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { TabContext } from '../components/TabbedLayout';
import withAuth from '../common/auth';

const Spinner = () => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
    <div className="flex flex-col items-center gap-3">
      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
      <p className="text-indigo-600 font-medium">Loading patient details...</p>
    </div>
  </div>
);

const StatCard = ({ title, value, icon: Icon, color = "indigo" }) => {
  const colorClasses = {
    indigo: "bg-indigo-50 border-indigo-100 text-indigo-600",
    green: "bg-green-50 border-green-100 text-green-600",
    blue: "bg-blue-50 border-blue-100 text-blue-600",
    red: "bg-red-50 border-red-100 text-red-600",
    purple: "bg-purple-50 border-purple-100 text-purple-600",
  };

  return (
    <div className={`rounded-lg border p-4 ${colorClasses[color]}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide opacity-75 mb-1">
            {title}
          </p>
          <p className="text-lg font-bold">{value}</p>
        </div>
        {Icon && <Icon size={24} className="opacity-50" />}
      </div>
    </div>
  );
};

function PatientDetails() {
  const tabContext = useContext(TabContext);
  const token = localStorage.getItem('accessToken');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Extract patientId from tab path
  const activeTabPath = tabContext?.tabs?.find(t => t.id === tabContext?.activeTabId)?.path || '';
  const patientId = activeTabPath.includes('patientdetails/') 
    ? activeTabPath.split('patientdetails/')[1] 
    : null;
  
  // Patient Info - get from sessionStorage if available
  const [patientInfo, setPatientInfo] = useState(() => {
    if (patientId) {
      const stored = sessionStorage.getItem(`patient_${patientId}`);
      if (stored) {
        try {
          return JSON.parse(stored);
        } catch (e) {
          return null;
        }
      }
    }
    return null;
  });
  
  // OP History
  const [opHistory, setOpHistory] = useState({
    dueBillSummary: [],
    recentAppointments: [],
    visitSummary: [],
    upAppoiment: []
  });
  
  // Lab History
  const [labHistory, setLabHistory] = useState([]);
  
  // Pharmacy History
  const [pharmacyHistory, setPharmacyHistory] = useState([]);
  const [invoiceDetails, setInvoiceDetails] = useState({});
  const [expandedBills, setExpandedBills] = useState(new Set());
  
  // Privilege Cards
  const [privilegeCards, setPrivilegeCards] = useState([]);

  useEffect(() => {
    if (patientId) {
      fetchOPHistory();
      fetchLabHistory();
      fetchPharmacyHistory();
      fetchPrivilegeCards();
    }
  }, [patientId]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount || 0).toFixed(2)}`;
  };

  const getVisitTypeLabel = (recType) => {
    if (!recType) return 'N/A';
    const recTypeUpper = recType.toUpperCase();
    if (recTypeUpper === 'APPOINTMENT' || recTypeUpper.includes('APPOINTMENT')) {
      return 'Doctor visit';
    } else if (recTypeUpper === 'OP' || recTypeUpper.includes('OP')) {
      return 'Nursing service';
    }
    return recType; // Return original if no match
  };

  const fetchOPHistory = async () => {
    if (!patientId) return;
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}${GET_PATIENT_TRACK}`, {
        headers: {
          'PatientID': patientId,
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`
        },
      });

      if (response.status === 200) {
        const data = response.data.data || {};
        console.log(data);
        setOpHistory({
          dueBillSummary: data.dueBillSummary || [],
          recentAppointments: data.recentAppointments || [],
          visitSummary: data.visitSummary || [],
          upAppoiment: data.upAppoiment || []
        });

        // Set patient info from first appointment only if not already set
        if (!patientInfo && data.recentAppointments?.length > 0) {
          const firstAppt = data.recentAppointments[0];
          setPatientInfo({
            PatientID: firstAppt.PatientID || patientId,
            PatientName: firstAppt.PatientName,
            Age: firstAppt.Age,
            Gender: firstAppt.Gender,
            MobileNo: firstAppt.MobileNo,
            Place: firstAppt.Place,
            Month: firstAppt.Month,
            Days: firstAppt.Days
          });
        }
      }
    } catch (error) {
      console.error('Error fetching OP history:', error);
      toast.error('Failed to fetch OP history', { autoClose: 2000 });
    } finally {
      setLoading(false);
    }
  };

  const fetchLabHistory = async () => {
    if (!patientId) return;
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}${GET_PATIENT_HISTORY}`, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
          'patientID': patientId,
        },
      });

      if (response.status === 200) {
        const data = response.data.data || [];
        // Sort by EntryDate (newest first)
        const sortedData = data.sort((a, b) => {
          const dateA = new Date(a.EntryDate || 0);
          const dateB = new Date(b.EntryDate || 0);
          return dateB - dateA; // Descending order (newest first)
        });
        setLabHistory(sortedData);
      }
    } catch (error) {
      console.error('Error fetching lab history:', error);
      toast.error('Failed to fetch lab history', { autoClose: 2000 });
    } finally {
      setLoading(false);
    }
  };

  const fetchPharmacyHistory = async () => {
    if (!patientId) return;
    setLoading(true);
    try {
      // Convert date strings to Date objects and format as YYYY-MM-DD
      const fromDate = new Date('2020-01-01');
      const toDate = new Date();
      
      // Format date as YYYY-MM-DD
      const formatDateForAPI = (date) => {
        if (!date) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      
      const response = await axios.get(`${BASE_URL}${GET_PHARMACY_SALES_REPORT}`, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
        },
        params: {
          'rpType': 'Patient',
          'fromDate': formatDateForAPI(fromDate),
          'toDate': formatDateForAPI(toDate),
          'searchKey': patientId,
          'patientId': 0,
          'itemNo': 0,
        },
      });

      if (response.status === 200) {
        const data = response.data.data || [];
        // Sort by EntryDate (newest first)
        const sortedData = data.sort((a, b) => {
          const dateA = new Date(a.EntryDate || 0);
          const dateB = new Date(b.EntryDate || 0);
          return dateB - dateA; // Descending order (newest first)
        });
        setPharmacyHistory(sortedData);
      }
    } catch (error) {
      console.error('Error fetching pharmacy history:', error);
      toast.error('Failed to fetch pharmacy history', { autoClose: 2000 });
    } finally {
      setLoading(false);
    }
  };

  const fetchPrivilegeCards = async () => {
    if (!patientId) return;
    try {
      const patientIdInt = parseInt(patientId, 10);
      if (isNaN(patientIdInt)) {
        console.error('Invalid patient ID:', patientId);
        return;
      }
      
      const response = await axios.get(`${BASE_URL}${GET_CARDS}`, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
          'patientID': patientIdInt,
        },
      });

      if (response.status === 200 && response.data.isSucess) {
        const data = response.data.data || [];
        setPrivilegeCards(data);
      }
    } catch (error) {
      console.error('Error fetching privilege cards:', error);
      // Don't show error toast for cards as it's not critical
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: FiUser },
    { id: 'ophistory', label: 'OP History', icon: FiFileText },
    { id: 'labhistory', label: 'Lab History', icon: FiActivity },
    { id: 'pharmacyhistory', label: 'Pharmacy History', icon: FiPackage },
  ];

  const fetchInvoiceDetails = async (bill) => {
    const invoiceKey = `${bill.Sequence}_${bill.InvoiceNo}`;
    
    // If details already fetched, just toggle expansion
    if (invoiceDetails[invoiceKey]) {
      toggleBillExpansion(bill);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}${GET_INVOICE_DETAILS}`, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
        },
        params: {
          Sequence: bill.Sequence,
          InvoiceNo: bill.InvoiceNo.toString(),
        }
      });

      if (response.status === 200) {
        const invoiceData = response.data.data;
        if (invoiceData && invoiceData.details) {
          setInvoiceDetails(prev => ({
            ...prev,
            [invoiceKey]: invoiceData
          }));
          // Expand the bill after fetching details
          setExpandedBills(prev => new Set([...prev, invoiceKey]));
        }
      }
    } catch (error) {
      console.error('Error fetching invoice details:', error);
      toast.error('Failed to fetch bill details', { autoClose: 2000 });
    } finally {
      setLoading(false);
    }
  };

  const toggleBillExpansion = (bill) => {
    const invoiceKey = `${bill.Sequence}_${bill.InvoiceNo}`;
    setExpandedBills(prev => {
      const newSet = new Set(prev);
      if (newSet.has(invoiceKey)) {
        newSet.delete(invoiceKey);
      } else {
        newSet.add(invoiceKey);
      }
      return newSet;
    });
  };

  const formatDateForExport = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  const exportToExcel = () => {
    try {
      if (!patientInfo) {
        toast.error('Patient information not available', { autoClose: 2000 });
        return;
      }

      const wb = XLSX.utils.book_new();

      // 1. Patient Information Sheet
      const patientInfoData = [{
        'Patient ID': patientInfo.PatientID || 'N/A',
        'Patient Name': patientInfo.PatientName || 'N/A',
        'Mobile Number': patientInfo.MobileNo || 'N/A',
        'Place': patientInfo.Place || 'N/A',
        'Age': patientInfo.Age || 0,
        'Month': patientInfo.Month || 0,
        'Days': patientInfo.Days || 0,
        'Gender': patientInfo.Gender || 'N/A',
        'DOB': patientInfo.DOB ? formatDateForExport(patientInfo.DOB) : 'N/A',
      }];
      const patientWs = XLSX.utils.json_to_sheet(patientInfoData);
      XLSX.utils.book_append_sheet(wb, patientWs, 'Patient Info');

      // 2. OP Appointments Sheet
      const opAppointmentsData = opHistory.recentAppointments.map(appt => {
        const validDate = new Date(appt.ValidUpto);
        const today = new Date();
        const isExpired = validDate < today;
        return {
          'Date': formatDateForExport(appt.EntryDate),
          'Doctor': appt.Ref_DoctorName || 'N/A',
          'OP Number': appt.InvoiceNo || 'N/A',
          'Valid Upto': formatDateForExport(appt.ValidUpto),
          'Status': isExpired ? 'Expired' : 'Valid',
          'Amount': parseFloat(appt.GrandTotal || 0).toFixed(2),
        };
      });
      if (opAppointmentsData.length > 0) {
        const opWs = XLSX.utils.json_to_sheet(opAppointmentsData);
        XLSX.utils.book_append_sheet(wb, opWs, 'OP Appointments');
      }

      // 3. Pending Bills Sheet
      const pendingBillsData = opHistory.dueBillSummary.map(bill => ({
        'Transaction Type': bill.TransType || 'General',
        'Date': formatDateForExport(bill.EntryDate),
        'Balance Due': parseFloat(bill.BalanceDue || 0).toFixed(2),
      }));
      if (pendingBillsData.length > 0) {
        const billsWs = XLSX.utils.json_to_sheet(pendingBillsData);
        XLSX.utils.book_append_sheet(wb, billsWs, 'Pending Bills');
      }

      // 4. Visit Summary Sheet
      const visitSummaryData = opHistory.visitSummary.map(visit => ({
        'Date': formatDateForExport(visit.EntryDate),
        'Department': visit.Department || 'N/A',
        'Visit Type': getVisitTypeLabel(visit.Department),
      }));
      if (visitSummaryData.length > 0) {
        const visitWs = XLSX.utils.json_to_sheet(visitSummaryData);
        XLSX.utils.book_append_sheet(wb, visitWs, 'Visit Summary');
      }

      // 5. Upcoming Appointments Sheet
      const upcomingData = opHistory.upAppoiment.map(appt => ({
        'Doctor': appt.Doctor || 'N/A',
        'Booking Date': formatDateForExport(appt.BookingDate),
        'Booking Time': appt.BookingTime || 'N/A',
        'Token': appt.Token || 'N/A',
      }));
      if (upcomingData.length > 0) {
        const upcomingWs = XLSX.utils.json_to_sheet(upcomingData);
        XLSX.utils.book_append_sheet(wb, upcomingWs, 'Upcoming Appointments');
      }

      // 6. Lab History Sheet
      const labData = labHistory.map(bill => ({
        'Invoice No': bill.InvoiceNo || 'N/A',
        'Date': formatDateForExport(bill.EntryDate),
        'Doctor': bill.Ref_DoctorName || 'N/A',
        'Total': parseFloat(bill.GrandTotal || 0).toFixed(2),
        'Paid': parseFloat((bill.CashAmount || 0) + (bill.BankAmount || 0)).toFixed(2),
        'Balance': parseFloat(bill.BalanceDue || 0).toFixed(2),
        'Status': bill.BalanceDue > 0 ? 'Pending' : 'Paid',
      }));
      if (labData.length > 0) {
        const labWs = XLSX.utils.json_to_sheet(labData);
        XLSX.utils.book_append_sheet(wb, labWs, 'Lab History');
      }

      // 7. Pharmacy History Sheet
      const pharmacyData = pharmacyHistory.map(bill => {
        const grandTotal = (parseFloat(bill.TotalAmount || 0) - parseFloat(bill.DiscountAmount || 0));
        const paidAmount = (parseFloat(bill.CashAmount || 0) + parseFloat(bill.CardAmount || 0) + parseFloat(bill.BankAmount || 0));
        const balanceDue = parseFloat(bill.BalanceDue || 0);
        return {
          'Bill No': bill.InvoiceNo || 'N/A',
          'Date': bill.EntryDate ? (bill.EntryDate.includes('T') ? bill.EntryDate.split('T')[0] : bill.EntryDate) : 'N/A',
          'Doctor': bill.DoctorName || 'N/A',
          'Grand Total': grandTotal.toFixed(2),
          'Paid': paidAmount.toFixed(2),
          'Balance': balanceDue.toFixed(2),
          'Status': balanceDue > 0 ? 'Pending' : 'Paid',
        };
      });
      if (pharmacyData.length > 0) {
        const pharmacyWs = XLSX.utils.json_to_sheet(pharmacyData);
        XLSX.utils.book_append_sheet(wb, pharmacyWs, 'Pharmacy History');
      }

      // 8. Privilege Cards Sheet
      const privilegeCardsData = privilegeCards.map(card => ({
        'Card Name': card.CardName || 'N/A',
        'Card Number': card.CardNumber || 'N/A',
        'Card Amount': parseFloat(card.CardAmount || 0).toFixed(2),
      }));
      if (privilegeCardsData.length > 0) {
        const cardsWs = XLSX.utils.json_to_sheet(privilegeCardsData);
        XLSX.utils.book_append_sheet(wb, cardsWs, 'Privilege Cards');
      }

      // Generate filename with local date (IST)
      const patientName = patientInfo.PatientName?.replace(/\s+/g, '_') || 'Patient';
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const filename = `${patientName}_Details_${year}-${month}-${day}.xlsx`;

      // Save file
      XLSX.writeFile(wb, filename);
      toast.success('Excel file exported successfully', { autoClose: 2000 });
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('Failed to export to Excel', { autoClose: 2000 });
    }
  };

  const exportToPDF = () => {
    try {
      if (!patientInfo) {
        toast.error('Patient information not available', { autoClose: 2000 });
        return;
      }

      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      let startY = 20;

      // Header
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('Patient Details Report', pageWidth / 2, startY, { align: 'center' });
      startY += 10;

      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(`Patient: ${patientInfo.PatientName || 'N/A'}`, pageWidth / 2, startY, { align: 'center' });
      startY += 6;
      doc.setFontSize(10);
      doc.text(`Generated on: ${new Date().toLocaleString()}`, pageWidth / 2, startY, { align: 'center' });
      startY += 10;

      // Patient Information Section
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Patient Information', 14, startY);
      startY += 8;

      const patientInfoData = [
        ['Patient ID', patientInfo.PatientID || 'N/A'],
        ['Name', patientInfo.PatientName || 'N/A'],
        ['Mobile', patientInfo.MobileNo || 'N/A'],
        ['Place', patientInfo.Place || 'N/A'],
        ['Age', `${patientInfo.Age || 0}Y ${patientInfo.Month || 0}M ${patientInfo.Days || 0}D`],
        ['Gender', patientInfo.Gender || 'N/A'],
        ['DOB', patientInfo.DOB ? formatDateForExport(patientInfo.DOB) : 'N/A'],
      ];

      autoTable(doc, {
        body: patientInfoData,
        startY: startY,
        theme: 'grid',
        styles: { fontSize: 10, cellPadding: 3 },
        columnStyles: { 0: { fontStyle: 'bold', cellWidth: 50 }, 1: { cellWidth: 140 } },
        margin: { left: 14, right: 14 },
      });
      startY = doc.lastAutoTable.finalY + 10;

      // OP Appointments Section
      if (opHistory.recentAppointments.length > 0) {
        if (startY > 250) {
          doc.addPage();
          startY = 20;
        }
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('OP Appointments', 14, startY);
        startY += 8;

        const opTableData = opHistory.recentAppointments.map(appt => {
          const validDate = new Date(appt.ValidUpto);
          const today = new Date();
          const isExpired = validDate < today;
          return [
            formatDateForExport(appt.EntryDate),
            appt.Ref_DoctorName || 'N/A',
            appt.InvoiceNo || 'N/A',
            formatDateForExport(appt.ValidUpto),
            isExpired ? 'Expired' : 'Valid',
            `₹${parseFloat(appt.GrandTotal || 0).toFixed(2)}`,
          ];
        });

        autoTable(doc, {
          head: [['Date', 'Doctor', 'OP No', 'Valid Upto', 'Status', 'Amount']],
          body: opTableData,
          startY: startY,
          styles: { fontSize: 8, cellPadding: 2 },
          headStyles: { fillColor: [99, 102, 241], textColor: 255, fontStyle: 'bold' },
          alternateRowStyles: { fillColor: [249, 250, 251] },
          margin: { left: 14, right: 14 },
        });
        startY = doc.lastAutoTable.finalY + 10;
      }

      // Pending Bills Section
      if (opHistory.dueBillSummary.length > 0) {
        if (startY > 250) {
          doc.addPage();
          startY = 20;
        }
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('Pending Bills', 14, startY);
        startY += 8;

        const billsTableData = opHistory.dueBillSummary.map(bill => [
          bill.TransType || 'General',
          formatDateForExport(bill.EntryDate),
          `₹${parseFloat(bill.BalanceDue || 0).toFixed(2)}`,
        ]);

        autoTable(doc, {
          head: [['Type', 'Date', 'Balance Due']],
          body: billsTableData,
          startY: startY,
          styles: { fontSize: 8, cellPadding: 2 },
          headStyles: { fillColor: [220, 38, 38], textColor: 255, fontStyle: 'bold' },
          alternateRowStyles: { fillColor: [254, 242, 242] },
          margin: { left: 14, right: 14 },
        });
        startY = doc.lastAutoTable.finalY + 10;
      }

      // Lab History Section
      if (labHistory.length > 0) {
        if (startY > 250) {
          doc.addPage();
          startY = 20;
        }
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('Lab History', 14, startY);
        startY += 8;

        const labTableData = labHistory.map(bill => [
          bill.InvoiceNo || 'N/A',
          formatDateForExport(bill.EntryDate),
          bill.Ref_DoctorName || 'N/A',
          `₹${parseFloat(bill.GrandTotal || 0).toFixed(2)}`,
          `₹${parseFloat((bill.CashAmount || 0) + (bill.BankAmount || 0)).toFixed(2)}`,
          `₹${parseFloat(bill.BalanceDue || 0).toFixed(2)}`,
          bill.BalanceDue > 0 ? 'Pending' : 'Paid',
        ]);

        autoTable(doc, {
          head: [['Invoice No', 'Date', 'Doctor', 'Total', 'Paid', 'Balance', 'Status']],
          body: labTableData,
          startY: startY,
          styles: { fontSize: 8, cellPadding: 2 },
          headStyles: { fillColor: [34, 197, 94], textColor: 255, fontStyle: 'bold' },
          alternateRowStyles: { fillColor: [240, 253, 244] },
          margin: { left: 14, right: 14 },
        });
        startY = doc.lastAutoTable.finalY + 10;
      }

      // Pharmacy History Section
      if (pharmacyHistory.length > 0) {
        if (startY > 250) {
          doc.addPage();
          startY = 20;
        }
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('Pharmacy History', 14, startY);
        startY += 8;

        const pharmacyTableData = pharmacyHistory.map(bill => {
          const grandTotal = (parseFloat(bill.TotalAmount || 0) - parseFloat(bill.DiscountAmount || 0));
          const paidAmount = (parseFloat(bill.CashAmount || 0) + parseFloat(bill.CardAmount || 0) + parseFloat(bill.BankAmount || 0));
          const balanceDue = parseFloat(bill.BalanceDue || 0);
          return [
            bill.InvoiceNo || 'N/A',
            bill.EntryDate ? (bill.EntryDate.includes('T') ? bill.EntryDate.split('T')[0] : bill.EntryDate) : 'N/A',
            bill.DoctorName || 'N/A',
            `₹${grandTotal.toFixed(2)}`,
            `₹${paidAmount.toFixed(2)}`,
            `₹${balanceDue.toFixed(2)}`,
            balanceDue > 0 ? 'Pending' : 'Paid',
          ];
        });

        autoTable(doc, {
          head: [['Bill No', 'Date', 'Doctor', 'Grand Total', 'Paid', 'Balance', 'Status']],
          body: pharmacyTableData,
          startY: startY,
          styles: { fontSize: 8, cellPadding: 2 },
          headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
          alternateRowStyles: { fillColor: [239, 246, 255] },
          margin: { left: 14, right: 14 },
        });
        startY = doc.lastAutoTable.finalY + 10;
      }

      // Privilege Cards Section
      if (privilegeCards.length > 0) {
        if (startY > 250) {
          doc.addPage();
          startY = 20;
        }
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('Privilege Cards', 14, startY);
        startY += 8;

        const cardsTableData = privilegeCards.map(card => [
          card.CardName || 'N/A',
          card.CardNumber || 'N/A',
          `₹${parseFloat(card.CardAmount || 0).toFixed(2)}`,
        ]);

        autoTable(doc, {
          head: [['Card Name', 'Card Number', 'Card Amount']],
          body: cardsTableData,
          startY: startY,
          styles: { fontSize: 8, cellPadding: 2 },
          headStyles: { fillColor: [139, 92, 246], textColor: 255, fontStyle: 'bold' },
          alternateRowStyles: { fillColor: [250, 245, 255] },
          margin: { left: 14, right: 14 },
        });
      }

      // Generate filename with local date (IST)
      const patientName = patientInfo.PatientName?.replace(/\s+/g, '_') || 'Patient';
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const filename = `${patientName}_Details_${year}-${month}-${day}.pdf`;

      // Save file
      doc.save(filename);
      toast.success('PDF file exported successfully', { autoClose: 2000 });
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      toast.error('Failed to export to PDF', { autoClose: 2000 });
    }
  };

  if (!patientId) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-gray-500">No patient ID provided</p>
      </div>
    );
  }

  return (
    <div className="flex h-full bg-background">
      {loading && <Spinner />}
      
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => {
                  if (tabContext && tabContext.closeTab) {
                    tabContext.closeTab(tabContext.activeTabId);
                  }
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Go Back"
              >
                <FiArrowLeft size={20} className="text-gray-600" />
              </button>
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-indigo-500">
                  Patient Details
                </p>
                <h1 className="text-lg font-bold text-slate-800 flex items-center">
                  <FiUser className="mr-2" size={20} />
                  {patientInfo?.PatientName || 'Patient Details'}
                </h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={exportToExcel}
                className="inline-flex items-center gap-2 rounded-lg bg-green-500 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-green-600"
                title="Export to Excel"
              >
                <FiDownload size={16} />
                Excel
              </button>
              <button
                type="button"
                onClick={exportToPDF}
                className="inline-flex items-center gap-2 rounded-lg bg-red-500 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-red-600"
                title="Export to PDF"
              >
                <FiDownload size={16} />
                PDF
              </button>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-hidden px-4 py-4 flex flex-col">
          {/* Patient Info Card */}
          {patientInfo && (
            <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm mb-4 flex-shrink-0">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Name</p>
                  <p className="font-semibold text-gray-900">{patientInfo.PatientName || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Age</p>
                  <p className="font-semibold text-gray-900">{patientInfo.Age || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Gender</p>
                  <p className="font-semibold text-gray-900">{patientInfo.Gender || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Mobile</p>
                  <p className="font-semibold text-gray-900">{patientInfo.MobileNo || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Place</p>
                  <p className="font-semibold text-gray-900">{patientInfo.Place || 'N/A'}</p>
                </div>
              </div>
            </section>
          )}

          {/* Tabs */}
          <div className="flex gap-2 mb-4 flex-shrink-0 border-b border-gray-200">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
                    activeTab === tab.id
                      ? 'border-indigo-600 text-indigo-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Icon size={16} />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-auto min-h-0">
            {activeTab === 'overview' && (
              <div className="space-y-4">
                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <StatCard
                    title="OP Visits"
                    value={opHistory.recentAppointments.length}
                    icon={FiFileText}
                    color="indigo"
                  />
                  <StatCard
                    title="Lab Tests"
                    value={labHistory.length}
                    icon={FiActivity}
                    color="green"
                  />
                  <StatCard
                    title="Pharmacy Bills"
                    value={pharmacyHistory.length}
                    icon={FiPackage}
                    color="blue"
                  />
                  <StatCard
                    title="Pending Amount"
                    value={formatCurrency(
                      opHistory.dueBillSummary.reduce((sum, bill) => sum + (bill.BalanceDue || 0), 0)
                    )}
                    icon={TbCurrencyRupee}
                    color="red"
                  />
                </div>

                {/* Privilege Cards */}
                {privilegeCards.length > 0 && (
                  <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                    <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <FiCreditCard className="text-purple-600" size={18} />
                      Privilege Cards
                    </h3>
                    <div className="grid grid-cols-6 md:grid-cols-6 lg:grid-cols-6 gap-4">
                      {privilegeCards.map((card, index) => (
                        <div 
                          key={card.CardID || index} 
                          className="border border-purple-200 rounded-lg p-4 bg-gradient-to-br from-purple-50 to-indigo-50 hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <FiCreditCard className="text-purple-600" size={20} />
                              <h4 className="font-semibold text-gray-900">{card.CardName || 'N/A'}</h4>
                            </div>
                          </div>
                          <div className="space-y-1 mt-3">
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-gray-600">Card Number:</span>
                              <span className="text-sm font-medium text-gray-900">{card.CardNumber || 'N/A'}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recent Appointments */}
                {opHistory.recentAppointments.length > 0 && (
                  <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                    <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <FiClock className="text-indigo-600" size={18} />
                      Recent OP Appointments
                    </h3>
                    <div className="space-y-2">
                      {opHistory.recentAppointments.slice(0, 5).map((appt, index) => {
                        const validDate = new Date(appt.ValidUpto);
                        const today = new Date();
                        const isExpired = validDate < today;
                        
                        return (
                          <div key={index} className="border border-gray-200 rounded-lg p-3 hover:border-indigo-300 transition-colors">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <p className="font-medium text-gray-900 text-sm">{appt.Ref_DoctorName || 'N/A'}</p>
                                <div className="flex items-center gap-2 mt-1 flex-wrap">
                                  <span className="text-xs text-gray-600">
                                    {formatDate(appt.EntryDate)}
                                  </span>
                                  <span className={`text-xs px-2 py-0.5 rounded ${
                                    isExpired ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                                  }`}>
                                    Valid: {formatDate(appt.ValidUpto)}
                                  </span>
                                  <span className="text-xs text-gray-600">OP: {appt.InvoiceNo}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Pending Bills */}
                {opHistory.dueBillSummary.length > 0 && (
                  <div className="bg-white rounded-xl border border-red-200 p-4 shadow-sm">
                    <h3 className="font-semibold text-red-900 mb-4 flex items-center gap-2">
                      <TbCurrencyRupee className="text-red-600" size={18} />
                      Pending Bills
                    </h3>
                    <div className="space-y-2">
                      {opHistory.dueBillSummary.slice(0, 5).map((bill, index) => (
                        <div key={index} className="border border-red-200 rounded-lg p-3 bg-red-50">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-gray-900 text-sm">{bill.TransType || 'General'}</p>
                              <p className="text-xs text-gray-600 mt-1">
                                {formatDate(bill.EntryDate)}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-red-600">{formatCurrency(bill.BalanceDue)}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'ophistory' && (
              <div className="space-y-4">
                {/* Recent Appointments */}
                <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <FiFileText className="text-indigo-600" size={18} />
                    All OP Appointments
                  </h3>
                  {opHistory.recentAppointments.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th className="px-4 py-3 text-left font-semibold text-gray-700">Date</th>
                            <th className="px-4 py-3 text-left font-semibold text-gray-700">Doctor</th>
                            <th className="px-4 py-3 text-left font-semibold text-gray-700">OP Number</th>
                            <th className="px-4 py-3 text-left font-semibold text-gray-700">Valid Upto</th>
                            <th className="px-4 py-3 text-left font-semibold text-gray-700">Status</th>
                            <th className="px-4 py-3 text-right font-semibold text-gray-700">Amount</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {opHistory.recentAppointments.map((appt, index) => {
                            const validDate = new Date(appt.ValidUpto);
                            const today = new Date();
                            const isExpired = validDate < today;
                            
                            return (
                              <tr key={index} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-gray-900">{formatDate(appt.EntryDate)}</td>
                                <td className="px-4 py-3 text-gray-900">{appt.Ref_DoctorName || 'N/A'}</td>
                                <td className="px-4 py-3 text-gray-900 font-medium">{appt.InvoiceNo}</td>
                                <td className="px-4 py-3 text-gray-900">{formatDate(appt.ValidUpto)}</td>
                                <td className="px-4 py-3">
                                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                                    isExpired ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                                  }`}>
                                    {isExpired ? 'Expired' : 'Valid'}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-right font-semibold text-gray-900">
                                  {formatCurrency(appt.GrandTotal)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <FiFileText className="mx-auto text-gray-400 mb-4" size={48} />
                      <p className="text-gray-500 font-medium">No OP appointments found</p>
                    </div>
                  )}
                </div>

                {/* Visit Summary */}
                {opHistory.visitSummary.length > 0 && (
                  <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                    <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <FiActivity className="text-indigo-600" size={18} />
                      Last Visit Summary
                    </h3>
                    <div className="space-y-2">
                      {opHistory.visitSummary.map((visit, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-gray-900 text-sm">{getVisitTypeLabel(visit.Department)}</p>
                              <p className="text-xs text-gray-600 mt-1">
                                {formatDate(visit.EntryDate)}
                              </p>
                            </div>
                            {visit.Department && (
                              <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded text-xs font-medium">
                                {visit.Department}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Upcoming Appointments */}
                {opHistory.upAppoiment.length > 0 && (
                  <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                    <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <FiClock className="text-indigo-600" size={18} />
                      Upcoming Appointments
                    </h3>
                    <div className="space-y-2">
                      {opHistory.upAppoiment.map((appt, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-gray-900 text-sm">{appt.Doctor || 'N/A'}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-xs text-gray-600">
                                  {formatDate(appt.BookingDate)}
                                </span>
                                <span className="text-xs text-gray-600">{appt.BookingTime}</span>
                                <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">
                                  Token: {appt.Token}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'labhistory' && (
              <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <FiActivity className="text-green-600" size={18} />
                  Lab Test History
                </h3>
                {labHistory.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-3 text-left font-semibold text-gray-700">Invoice No</th>
                          <th className="px-4 py-3 text-left font-semibold text-gray-700">Date</th>
                          <th className="px-4 py-3 text-left font-semibold text-gray-700">Doctor</th>
                          <th className="px-4 py-3 text-right font-semibold text-gray-700">Total</th>
                          <th className="px-4 py-3 text-right font-semibold text-gray-700">Paid</th>
                          <th className="px-4 py-3 text-right font-semibold text-gray-700">Balance</th>
                          <th className="px-4 py-3 text-center font-semibold text-gray-700">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {labHistory.map((bill, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-gray-900 font-medium">{bill.InvoiceNo || 'N/A'}</td>
                            <td className="px-4 py-3 text-gray-900">{formatDate(bill.EntryDate)}</td>
                            <td className="px-4 py-3 text-gray-900">{bill.Ref_DoctorName || 'N/A'}</td>
                            <td className="px-4 py-3 text-right font-semibold text-gray-900">
                              {formatCurrency(bill.GrandTotal)}
                            </td>
                            <td className="px-4 py-3 text-right text-green-600 font-medium">
                              {formatCurrency((bill.CashAmount || 0) + (bill.BankAmount || 0))}
                            </td>
                            <td className="px-4 py-3 text-right text-red-600 font-medium">
                              {formatCurrency(bill.BalanceDue)}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {bill.BalanceDue > 0 ? (
                                <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium">
                                  Pending
                                </span>
                              ) : (
                                <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                                  Paid
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <FiActivity className="mx-auto text-gray-400 mb-4" size={48} />
                    <p className="text-gray-500 font-medium">No lab test history found</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'pharmacyhistory' && (
              <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <FiPackage className="text-blue-600" size={18} />
                  Pharmacy Purchase History
                </h3>
                {pharmacyHistory.length > 0 ? (
                  <div className="grid grid-cols-1 gap-4">
                    {pharmacyHistory.map((bill, index) => {
                      const invoiceKey = `${bill.Sequence}_${bill.InvoiceNo}`;
                      const details = invoiceDetails[invoiceKey];
                      const isExpanded = expandedBills.has(invoiceKey);
                      
                      // Calculate grand total: TotalAmount - DiscountAmount (matching SalesEntry.js line 3991)
                      const grandTotal = (parseFloat(bill.TotalAmount || 0) - parseFloat(bill.DiscountAmount || 0));
                      const paidAmount = (parseFloat(bill.CashAmount || 0) + parseFloat(bill.CardAmount || 0) + parseFloat(bill.BankAmount || 0));
                      const balanceDue = parseFloat(bill.BalanceDue || 0);
                      const totalAmount = parseFloat(bill.TotalAmount || 0);
                      const discountAmount = parseFloat(bill.DiscountAmount || 0);
                      const actualGrandTotal = totalAmount + discountAmount;
                      
                      return (
                        <div 
                          key={`${bill.InvoiceNo}_${index}`}
                          className="bg-white rounded-xl border-2 border-slate-200 shadow-md hover:shadow-lg transition-all duration-200 overflow-hidden"
                        >
                          {/* Bill Header */}
                          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-4 border-b border-slate-200">
                            <div className="flex items-center justify-between gap-4">
                              {/* Left Side: Invoice Info and Doctor */}
                              <div className="flex items-center gap-4 flex-1">
                                <div className="bg-indigo-600 text-white px-3 py-1 rounded-lg font-semibold text-sm">
                                  #{bill.InvoiceNo}
                                </div>
                                <div className="flex items-center gap-2 text-sm text-slate-600">
                                  <FiCalendar className="w-4 h-4" />
                                  <span>
                                    {bill.EntryDate ? (bill.EntryDate.includes('T') ? bill.EntryDate.split('T')[0] : bill.EntryDate) : 'N/A'}
                                  </span>
                                </div>
                                {bill.DoctorName && (
                                  <div className="text-sm text-slate-600">
                                    <span className="text-slate-500">Doctor: </span>
                                    <span className="font-medium">{bill.DoctorName}</span>
                                  </div>
                                )}
                              </div>
                              {/* Right Side: Total Amount, Pending Status, and Show Details Button */}
                              <div className="flex items-center gap-4">
                                <div className="text-right">
                                  <p className="text-xs text-slate-500">Total Amount</p>
                                  <p className="text-xl font-bold text-indigo-600">{formatCurrency(totalAmount)}</p>
                                </div>
                                {balanceDue > 0 ? (
                                  <span className="px-3 py-1.5 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded-full whitespace-nowrap">
                                    Pending: {formatCurrency(balanceDue)}
                                  </span>
                                ) : (
                                  <span className="px-3 py-1.5 bg-green-100 text-green-800 text-xs font-semibold rounded-full whitespace-nowrap">
                                    Paid
                                  </span>
                                )}
                                <button
                                  onClick={() => fetchInvoiceDetails(bill)}
                                  className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
                                >
                                  {isExpanded ? (
                                    <>
                                      <FiChevronUp size={16} />
                                      Hide Details
                                    </>
                                  ) : (
                                    <>
                                      <FiChevronDown size={16} />
                                      Show Details
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>

                          {/* Bill Summary */}
                          <div className="p-4 border-b border-slate-200">
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                              <div>
                                <p className="text-xs text-slate-500 mb-1">Grand Total</p>
                                <p className="text-sm font-semibold text-slate-900">{formatCurrency(actualGrandTotal)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-slate-500 mb-1">Tax Amount</p>
                                <p className="text-sm font-semibold text-slate-900">{formatCurrency(bill.TaxAmount || 0)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-slate-500 mb-1">Discount</p>
                                <p className="text-sm font-semibold text-green-600">{formatCurrency(discountAmount)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-slate-500 mb-1">Total Amount</p>
                                <p className="text-sm font-semibold text-slate-900">{formatCurrency(totalAmount)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-slate-500 mb-1">Balance Due</p>
                                <p className={`text-sm font-semibold ${balanceDue > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                  {formatCurrency(balanceDue)}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Invoice Items - Accordion */}
                          {isExpanded && details && details.details && (
                            <div className="px-4 pb-4 border-t border-slate-200">
                              <div className="pt-4">
                                <h4 className="text-sm font-semibold text-slate-700 mb-4">
                                  Invoice Items ({details.details.length})
                                </h4>
                                <div className="overflow-x-auto">
                                  <table className="min-w-full divide-y divide-slate-200 text-xs">
                                    <thead className="bg-slate-100">
                                      <tr>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700">SI</th>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700">Item Name</th>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700">Batch</th>
                                        <th className="px-3 py-2 text-right text-xs font-semibold text-slate-700">Qty</th>
                                        <th className="px-3 py-2 text-right text-xs font-semibold text-slate-700">MRP</th>
                                        <th className="px-3 py-2 text-right text-xs font-semibold text-slate-700">Sales Price</th>
                                        <th className="px-3 py-2 text-right text-xs font-semibold text-slate-700">Sales Rate</th>
                                        <th className="px-3 py-2 text-right text-xs font-semibold text-slate-700">Total</th>
                                        <th className="px-3 py-2 text-right text-xs font-semibold text-slate-700">Disc %</th>
                                        <th className="px-3 py-2 text-right text-xs font-semibold text-slate-700">Disc Amt</th>
                                        <th className="px-3 py-2 text-right text-xs font-semibold text-slate-700">Tax Code</th>
                                        <th className="px-3 py-2 text-right text-xs font-semibold text-slate-700">Tax</th>
                                        <th className="px-3 py-2 text-right text-xs font-semibold text-slate-700">Net Total</th>
                                      </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-slate-200">
                                      {details.details.map((detail, detailIndex) => (
                                        <tr key={detailIndex} className="hover:bg-indigo-50/30">
                                          <td className="px-3 py-2 text-slate-700">{detailIndex + 1}</td>
                                          <td className="px-3 py-2 font-medium text-slate-900">{detail.ItemName || '-'}</td>
                                          <td className="px-3 py-2 text-slate-600">{detail.BatchCode || '-'}</td>
                                          <td className="px-3 py-2 text-right text-slate-700">{parseFloat(detail.Qty || 0).toFixed(2)}</td>
                                          <td className="px-3 py-2 text-right text-slate-700">₹{parseFloat(detail.MRP || 0).toFixed(2)}</td>
                                          <td className="px-3 py-2 text-right text-slate-700">₹{parseFloat(detail.SalesPrice || 0).toFixed(2)}</td>
                                          <td className="px-3 py-2 text-right text-slate-700">
                                            {(() => {
                                              const mrp = parseFloat(detail.MRP || 0);
                                              const taxCode = parseFloat(detail.TaxCode || 0);
                                              const salesRate = mrp - (mrp * (taxCode / 100));
                                              return `₹${salesRate.toFixed(2)}`;
                                            })()}
                                          </td>
                                          <td className="px-3 py-2 text-right text-slate-700">₹{parseFloat(detail.Total || 0).toFixed(2)}</td>
                                          <td className="px-3 py-2 text-right text-slate-700">{parseFloat(detail.Disc_Percentage || 0).toFixed(2)}%</td>
                                          <td className="px-3 py-2 text-right text-slate-700">₹{parseFloat(detail.Disc_Amount || 0).toFixed(2)}</td>
                                          <td className="px-3 py-2 text-right text-slate-700">{detail.TaxCode || '-'}</td>
                                          <td className="px-3 py-2 text-right text-slate-700">₹{parseFloat(detail.TaxAmount || 0).toFixed(2)}</td>
                                          <td className="px-3 py-2 text-right font-semibold text-slate-900">₹{(parseFloat(detail.NetTotal || 0) + parseFloat(detail.TaxAmount || 0)).toFixed(2)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <FiPackage className="mx-auto text-gray-400 mb-4" size={48} />
                    <p className="text-gray-500 font-medium">No pharmacy purchase history found</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
      </div>

      <ToastContainer
        position="top-center"
        autoClose={2000}
        limit={1}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Slide}
      />
    </div>
  );
}

export default withAuth(PatientDetails);

